<template>
  <!-- eslint-disable vue/no-v-model-argument -->
  <WdsPageSignup class="wds-page-signup-login" nav-prev @prev="back">
    <template #prev-text>
      <span>{{ $t('Previous') }}</span>
    </template>

    <template #left>
      <template v-if="inviter.company !== undefined && inviter.company !== null">
        <LeftRegisterInvited :inviter="inviter"></LeftRegisterInvited>
      </template>
      <slot v-else name="left" />
    </template>
    <template #nav-right>
      <AuthLanguageSwitcher />
    </template>

    <template #right>
      <WdsHeading as="h1" type="title1">
        {{ title }}
      </WdsHeading>

      <BaseHtmlForm
        class="!tw-mt-[32px]"
        action="/register"
        method="POST"
        novalidate
        @form-submitted="onFormSubmitted"
      >
        <slot name="inputs-hidden" />

        <WdsInput
          v-model="v$.email.$model"
          :error="errorEmail"
          :info="emailInfoText"
          :label="$t('Email')"
          lg
          name="email"
          :placeholder="$t('Enter your email')"
          autocomplete="email"
          autofocus
          required
          type="email"
          data-test="register-email-input"
          @focusout="(v$.email.touch, (v$.email.focusedOut = true))"
        />

        <div class="!tw-mt-[24px]">
          <WdsInput
            v-model="v$.password.$model"
            :error="errorPassword"
            :label="$t('Password')"
            lg
            name="password"
            :placeholder="$t('Create new password')"
            autocomplete="new-password"
            required
            type="password"
            data-test="register-password-input"
            @focusout="v$.password.focusedOut = true"
          />
        </div>

        <div class="!tw-mt-[32px]">
          <WdsCheckbox
            v-model:checked="v$.acceptTerms.$model"
            name="accept_terms"
            data-test="register-accept-terms"
            required
          >
            {{ $t("I agree to Worksome's") }}
            <a
              href="https://www.worksome.com/legal-center/term-of-service-introduction"
              target="_blank"
              class="hover:tw-underline"
            >
              {{ $t('Terms of Service') }}
            </a>
            {{ $t('and') }}
            <a
              href="https://www.worksome.com/legal-center/privacy-policy"
              target="_blank"
              class="hover:tw-underline"
            >
              {{ $t('Privacy Policy') }}
            </a>
          </WdsCheckbox>
          <WdsText v-if="errorAcceptTerms !== null" class="tw-mt-1">
            <span class="tw-text-red-500 tw-font-normal">
              {{ errorAcceptTerms }}
            </span>
          </WdsText>
        </div>

        <WdsButton
          class="!tw-mt-[24px] no-spinner"
          :disabled="v$.$invalid"
          full-width
          lg
          primary
          type="submit"
          data-test="register-submit"
          @click="onClickSubmit"
        >
          {{ $t('Get started') }}
        </WdsButton>
      </BaseHtmlForm>

      <div class="!tw-mt-[14px] login-bottom-links tw-text-base">
        {{ $t('Already have an account?') }}
        <a href="/login" class="hover:tw-underline">{{ $t('Log in') }}</a>
      </div>
    </template>
  </WdsPageSignup>

  <WdsModal
    ref="confirmCurrencyModal"
    :show="showEmailProviderAlert"
    :title="$t('Have you filled in your company email?')"
    @cancel="disableFreeEmailProviderAlert"
  >
    <WdsText>
      {{
        $t(
          "We've recognized that your email belongs to a standard email provider. We recommend that you update with your company email instead",
        )
      }}
    </WdsText>
    <WdsText secondary>{{ $t('- e.g. name@mycompany.com') }}</WdsText>
    <template #footer>
      <WdsStack justify-end>
        <WdsButton primary @click="disableFreeEmailProviderAlert">
          {{ $t('I understand') }}
        </WdsButton>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script>
import { computed, ref } from 'vue'
import useVuelidate from '@vuelidate/core'
import { email, helpers, minLength, required } from '@vuelidate/validators'
import {
  WdsButton,
  WdsCheckbox,
  WdsHeading,
  WdsInput,
  WdsPageSignup,
  WdsStack,
  WdsText,
  WdsModal,
} from '@worksome/wds-components'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import AuthLanguageSwitcher from '@/components/auth/AuthLanguageSwitcher.vue'
import { $t as trans } from '@/composables/translation'
import LeftRegisterInvited from '@/components/auth/left/LeftRegisterInvited.vue'

const MIN_PASSWORD_LENGTH = 8
const VARIANT_BLOCK_FREE_EMAIL = 'variantB'
const HASH = window.location.hash.substring(1) // Remove '#' at start

export default {
  name: 'RegisterPage',
  components: {
    BaseHtmlForm,
    AuthLanguageSwitcher,
    WdsButton,
    WdsCheckbox,
    WdsHeading,
    WdsInput,
    WdsPageSignup,
    WdsStack,
    WdsText,
    WdsModal,
    LeftRegisterInvited,
  },
  props: {
    errors: {
      type: Object,
      required: true,
    },
    isCompany: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    inviter: {
      type: Object,
      required: false,
      default: {},
    },
    prefilledEmail: {
      type: String,
      required: false,
      default: '',
    },
  },
  setup(props) {
    const v$ = useVuelidate()

    const showEmailProviderAlert = ref(false)
    const confirmedFreeEmail = ref(false)

    const errorAcceptTerms = computed(() => {
      const { $dirty, $errors, $invalid } = v$.value.acceptTerms
      if ($dirty && $invalid) {
        return $errors[0].$message
      }
      return null
    })

    const errorEmail = computed(() => {
      const { $dirty, $errors, $invalid, focusedOut } = v$.value.email

      if (!$dirty && props.errors.hasOwnProperty('email')) {
        return props.errors.email[0]
      }
      if (focusedOut && $dirty && $invalid) {
        return $errors[0].$message
      }
      return null
    })

    const errorPassword = computed(() => {
      const { $dirty, $errors, $invalid, focusedOut } = v$.value.password
      if (!$dirty && props.errors.hasOwnProperty('password')) {
        return props.errors.password[0]
      }
      if (focusedOut && $dirty && $invalid) {
        return $errors[0].$message
      }
      return null
    })

    const emailInfoText = computed(() =>
      props.isCompany && !v$.value.email.$invalid
        ? 'We recommend using company email - e.g. name@company.com'
        : false,
    )

    const isFreeEmail = computed(() => {
      const providers = [
        'gmail',
        'hotmail',
        'domain',
        'live',
        'yahoo',
        'outlook',
        'me',
        'mail',
        'icloud',
        'msn',
        'mail',
        'live',
        'mac',
        'googlemail',
        'jubii',
        'sol',
      ]

      for (const provider of providers) {
        const r = new RegExp(`^(.*?(@${provider}.)[^$]*)$`, 'g')
        if (r.test(v$.value.email.$model)) {
          return true
        }
      }

      return false
    })

    function disableFreeEmailProviderAlert() {
      showEmailProviderAlert.value = false
      if (HASH === VARIANT_BLOCK_FREE_EMAIL) {
        return
      }
      confirmedFreeEmail.value = true
    }

    function onClickSubmit() {
      v$.value.$touch()
    }

    function onFormSubmitted(event) {
      if (v$.value.$invalid) {
        event.preventDefault()
      }

      if (props.isCompany && isFreeEmail.value && !confirmedFreeEmail.value) {
        event.preventDefault()
        showEmailProviderAlert.value = true
      }
    }

    function back() {
      window.location.href = `${window.location.protocol}//${window.location.host}/register/choose-account-type`
    }

    return {
      back,
      errorAcceptTerms,
      errorEmail,
      errorPassword,
      onClickSubmit,
      onFormSubmitted,
      showEmailProviderAlert,
      confirmedFreeEmail,
      disableFreeEmailProviderAlert,
      emailInfoText,
      v$,
    }
  },
  data() {
    return {
      acceptTerms: false,
      email: this.prefilledEmail ?? '',
      password: '',
    }
  },
  validations() {
    return {
      acceptTerms: {
        accept: helpers.withMessage(trans('You must accept to sign up'), (value) => value === true),
      },
      email: {
        email: helpers.withMessage(trans('Please enter your email address'), email),
        required: helpers.withMessage(trans('Please enter your email address'), required),
        $lazy: true,
      },
      password: {
        minLength: helpers.withMessage(
          trans('The password must be at least :min characters.', {
            min: MIN_PASSWORD_LENGTH,
          }),
          minLength(MIN_PASSWORD_LENGTH),
        ),
        required: helpers.withMessage(
          trans('Remember to type in the password to your account'),
          required,
        ),
      },
    }
  },
}
</script>

<style lang="scss">
.wds-input-wrapper > .wds-input-label {
  margin-bottom: unset;
}
</style>
